import { KitchenDisplay } from 'kiolyn-api';
import { isEmpty } from 'lodash';
import { decorate, observable } from 'mobx';
import { useLocalStore } from 'mobx-react-lite';
import React, { createContext, FC, ReactElement, useContext } from 'react';
import { isNullOrUndefined } from 'util';
import { ViewState } from '../../domain/models/ViewState';
import IKitchenDisplayRepository from '../../domain/repositories/IKitchenDisplayRepository';
import IMainStationRepository from '../../domain/repositories/IMainStationRepository';
import IStationRepository from '../../domain/repositories/IStationRepository';
import MainStationRepository from '../../interfaces/MainStationRepository';
import { DSOrder } from './StatusTypeList.stories';

class KitchenDisplayStore {
  private readonly kitchenDisplayRepository?: IKitchenDisplayRepository;
  private readonly stationRepository?: IStationRepository;
  private mainStationRepository?: IMainStationRepository;
  private readonly storeid: string;
  private readonly kitchenDisplayid: string;

  private websocket?: WebSocket;

  // The main kitchenDisplay view state
  public viewState = ViewState.LOADING;
  // The current KitchenDisplay
  public kitchenDisplay: KitchenDisplay | undefined = {
    id: '1',
    name: 'Kitchen Display 1',
    type: 'kitchendisplay'
  };
  // The orders for status board
  public orders: DSOrder[] = [];
  public hasStatusBoard = false;

  constructor(
    storeid: string,
    kitchenDisplayid: string,
    kitchenDisplayRepository?: IKitchenDisplayRepository,
    stationRepository?: IStationRepository
  ) {
    this.kitchenDisplayRepository = kitchenDisplayRepository;
    this.stationRepository = stationRepository;
    this.storeid = storeid;
    this.kitchenDisplayid = kitchenDisplayid;
  }

  // Reload the kitchenDisplay based on given storeid and kitchenDisplayid
  public async reload(): Promise<void> {
    if (isNullOrUndefined(this.kitchenDisplayRepository)) {
      return;
    }

    this.viewState = ViewState.LOADING;
    try {
      this.kitchenDisplay = await this.kitchenDisplayRepository.loadKitchenDisplay(
        this.storeid,
        this.kitchenDisplayid
      );

      await this.loadMainStation();
      await this.listOrders();
      await this.connectToPC();

      this.viewState = ViewState.OK;
    } catch (e) {
      //console.error(e); for pass test
      this.viewState = ViewState.ERROR;
    }
  }

  public async saveKitchenDisplayOrder(
    order: DSOrder
  ): Promise<string | undefined> {
    return await this.mainStationRepository?.saveKitchenDisplayOrder(order);
  }

  private async loadMainStation() {
    if (this.stationRepository === undefined) {
      return;
    }

    let mainStation = await this.stationRepository.loadMainStation(
      this.storeid
    );
    if (
      mainStation === undefined ||
      mainStation.ip_address === undefined ||
      isEmpty(mainStation.ip_address)
    ) {
      console.error('Main station is not found');
      return;
    }

    console.log(`Main station ip: ${mainStation.ip_address}`);

    this.mainStationRepository = new MainStationRepository(
      mainStation.ip_address,
      this.storeid
    );
  }

  private async listOrders() {
    if (this.mainStationRepository === undefined) {
      return;
    }

    try {
      this.orders = (await this.mainStationRepository.listKitchenDisplayOrders(
        this.kitchenDisplayid
      )) as DSOrder[];
      this.orders = this.sortKitchenOrders(this.orders);
    } catch (e) {
      console.error(e);
      this.viewState = ViewState.ERROR;
    }
  }

  private async connectToPC() {
    if (this.mainStationRepository === undefined) {
      return;
    }

    let connectInterval: NodeJS.Timeout;
    this.websocket = this.mainStationRepository.getWebsocket();

    this.websocket.onopen = () => {
      clearTimeout(connectInterval);
      console.log('Connect to main station successfully');
    };

    // websocket onclose event listener
    this.websocket.onclose = e => {
      connectInterval = setTimeout(() => {
        if (this.websocket?.readyState === WebSocket.CLOSED) {
          this.connectToPC();
        }
      }, 1000);
    };

    this.websocket.onmessage = async message => {
      const data = JSON.parse(message.data);
      if (
        data.type === 'OrderChanged' &&
        data.content.includes('_') &&
        this.mainStationRepository !== undefined
      ) {
        console.log(`Receive order from main station: ${data.content}`);

        const order = (await this.mainStationRepository.loadKitchenDisplayOrderById(
          this.kitchenDisplayid,
          data.content
        )) as DSOrder;

        if (order !== null && order !== undefined) {
          const index = this.orders.findIndex(o => o.id === order.id);
          if (index < 0) {
            this.orders.push(order);
          } else {
            this.orders[index] = order;
          }
          this.orders = [...this.orders];
        } else {
          const index = this.orders.findIndex(o => o.id === data.content);
          if (index > 0) {
            this.orders.splice(index, 1);
          }
          this.orders = [...this.orders];
        }
        this.orders = this.sortKitchenOrders(this.orders);
      } else if (
        data.type === 'ActiveShiftChanged' &&
        data.content.includes('close')
      ) {
        await this.listOrders();
      }
    };

    // websocket onerror event listener
    this.websocket.onerror = err => {
      this.websocket?.close();
    };
  }

  public sortKitchenOrders(orders: DSOrder[] | undefined): DSOrder[] {
    if (!orders || orders.length === 0) {
      return [];
    }

    return orders.sort((a, b) => {
      if (a.order_no && b.order_no) {
        return a.order_no - b.order_no;
      }
      return a.id.localeCompare(b.id);
    });
  }
}

decorate(KitchenDisplayStore, {
  viewState: observable,
  kitchenDisplay: observable,
  orders: observable
});

interface KitchenDisplayProviderProps {
  children: ReactElement;
  kitchenDisplayRepository: IKitchenDisplayRepository;
  stationRepository?: IStationRepository;
  storeid: string;
  kitchenDisplayId: string;
}

export const KitchenDisplayProvider: FC<KitchenDisplayProviderProps> = ({
  children,
  kitchenDisplayRepository,
  stationRepository,
  storeid,
  kitchenDisplayId
}: KitchenDisplayProviderProps) => {
  const store = useLocalStore(
    () =>
      new KitchenDisplayStore(
        storeid,
        kitchenDisplayId,
        kitchenDisplayRepository,
        stationRepository
      )
  );
  store.reload();
  return (
    <KitchenDisplayContext.Provider value={store}>
      {children}
    </KitchenDisplayContext.Provider>
  );
};

export const KitchenDisplayContext = createContext(
  new KitchenDisplayStore('', '')
);
export const useKitchenDisplay = () => useContext(KitchenDisplayContext);
